import { useState } from 'react';
import { Check, Copy, Printer, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { formatClaimCode } from '@/lib/claimCode';
import { cn } from '@/lib/cn';

export interface SecretRow {
  label: string;
  value: string;
  /** Render with claim-code grouping. */
  isClaimCode?: boolean;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard blocked; the value is on screen to read. */
    }
  }

  return (
    <Button size="sm" variant="ghost" onClick={copy} aria-label={`Copy ${label}`}>
      {copied ? <Check className="size-4 text-ok" aria-hidden /> : <Copy className="size-4" aria-hidden />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

interface ShowOnceSecretProps {
  rows: SecretRow[];
  /** Prepended to the downloaded filename. */
  filename?: string;
  className?: string;
}

/**
 * Credentials the server will never show again.
 *
 * Claim codes and device secrets exist in plaintext exactly once, in the response
 * that creates them. If the operator navigates away without saving them the device
 * is unrecoverable, so this offers every plausible way of keeping them — copy,
 * print, download — and says plainly that there is no second chance.
 */
export function ShowOnceSecret({ rows, filename = 'autoaware-credentials', className }: ShowOnceSecretProps) {
  function copyAll() {
    const text = rows.map((row) => `${row.label}: ${row.value}`).join('\n');
    void navigator.clipboard.writeText(text).catch(() => undefined);
  }

  function downloadCsv() {
    const header = 'label,value\n';
    const body = rows.map((row) => `"${row.label}","${row.value}"`).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-start gap-2.5 rounded-[--radius-control] bg-warn-soft px-3 py-2.5">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
        <p className="text-[13px] text-ink-secondary">
          <span className="font-medium text-ink">Save these now.</span> They are shown once and
          cannot be retrieved later.
        </p>
      </div>

      <ul className="divide-y divide-line rounded-[--radius-control] border border-line">
        {rows.map((row) => (
          <li key={row.label} className="flex items-center justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-[12px] text-ink-muted">{row.label}</p>
              <p className="truncate font-mono text-[13px] text-ink">
                {row.isClaimCode ? formatClaimCode(row.value) : row.value}
              </p>
            </div>
            <div className="print-hidden shrink-0">
              <CopyButton value={row.value} label={row.label} />
            </div>
          </li>
        ))}
      </ul>

      <div className="print-hidden flex flex-wrap gap-2">
        <Button size="sm" onClick={copyAll}>
          <Copy className="size-4" aria-hidden />
          Copy all
        </Button>
        <Button size="sm" onClick={downloadCsv}>
          Download CSV
        </Button>
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="size-4" aria-hidden />
          Print
        </Button>
      </div>
    </div>
  );
}
